import type { ListItemProps } from "@/components/ListItem/ListItem";
import type { MaterialNames } from "@/components/material_symbol_names";
import type { TagType } from "@/components/Tag/Tag";
import type { MentionUser } from "./types";

function mentionLabel(user: MentionUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
}

const PROGRAM_STATUS_TAGS: Record<string, { tagType: TagType; tagIconName?: MaterialNames }> = {
  Active: { tagType: "success" },
  Pending: { tagType: "warn", tagIconName: "schedule" },
  Suspended: { tagType: "warn" },
  Terminated: { tagType: "error" },
  Inactive: { tagType: "surface" },
  Deceased: { tagType: "surface" }
};

const EVENT_STATUS_TAGS: Record<string, { tagText: string; tagType: TagType }> = {
  Active: { tagText: "Active", tagType: "success" },
  Pending: { tagText: "Pending", tagType: "warn" },
  Draft: { tagText: "Draft", tagType: "information" },
  PUBLISHED: { tagText: "Published", tagType: "success" },
  Canceled: { tagText: "Canceled", tagType: "surface" },
  Cancelled: { tagText: "Canceled", tagType: "surface" },
  Completed: { tagText: "Completed", tagType: "surface" }
};

function isPersonAvatarRow(user: MentionUser): boolean {
  return !user.kind || user.kind === "teammate" || user.kind === "person";
}

function personSupportingText(user: MentionUser): string | undefined {
  if (user.kind === "person") {
    const lines = [user.classification, user.facilityName || user.sectionName].filter(Boolean);
    return lines.join("\n") || user.subtitle;
  }
  return user.email || user.subtitle;
}

function eventStatusTag(
  status?: string | null
): Pick<ListItemProps, "tagText" | "tagType" | "tagStyle" | "tagPosition"> {
  if (!status) return {};
  const mapped =
    EVENT_STATUS_TAGS[status] ?? EVENT_STATUS_TAGS[status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()];
  if (!mapped) return {};
  return { tagText: mapped.tagText, tagType: mapped.tagType, tagStyle: "strong", tagPosition: "trailing" };
}

export function mentionListItemProps(user: MentionUser): ListItemProps {
  const headline = mentionLabel(user);

  if (isPersonAvatarRow(user)) {
    const supportingText = personSupportingText(user);
    const status = user.programStatus ? PROGRAM_STATUS_TAGS[user.programStatus] : undefined;
    return {
      compact: true,
      useAvatar: true,
      picture: user.photo,
      iconName: "person",
      headline,
      supportingText,
      longContent: Boolean(supportingText && supportingText.includes("\n")),
      centerLeadingIcon: true,
      ...(status && user.programStatus
        ? {
            tagText: user.programStatus,
            tagType: status.tagType,
            tagIconName: status.tagIconName,
            tagStyle: "strong" as const,
            tagPosition: "trailing" as const
          }
        : {})
    };
  }

  if (user.kind === "facility") {
    return {
      compact: true,
      iconName: user.iconName || "store",
      iconBackground: true,
      overline: user.sectionName || undefined,
      headline,
      supportingText: user.subtitle,
      longContent: Boolean(user.sectionName && user.subtitle),
      centerLeadingIcon: true
    };
  }

  return {
    compact: true,
    iconName: user.iconName || "link",
    iconBackground: true,
    headline,
    supportingText: user.subtitle,
    centerLeadingIcon: true,
    ...(user.kind === "event" && user.expandContext?.type === "Event" ? eventStatusTag(user.expandContext.status) : {})
  };
}
